<template>
  <app-drawer :model-value="checklistPane.isOpen"
              size="sm"
              :persistent="false"
              hide-footer
              no-body-padding
              fill-body
              scrim="transparent"
              :tabs="displayTabs"
              v-model:tab="tab"
              data-locator="checklist-task-pane"
              @update:model-value="onClosePane"
              @cancel="onClosePane"
  >
    <template #title>
      <span :title="displayPaneTitle">{{ displayPaneTitle }}</span>
    </template>

    <template v-if="canNavigate" #header-action>
      <nav class="d-flex align-center">
        <AppTooltip :disabled="!previousItem">
          <template #activator="{ props }">
            <app-button :disabled="!previousItem"
                        data-locator="previous-item-nav-button"
                        type="icon"
                        v-bind="props"
                        @click="onPreviousItem"
            >
              <app-material-icon
                name="keyboard_arrow_up"
                size="sm"
                :color="previousItem ? 'icon-primary' : 'icon-disabled'"
              />
            </app-button>
          </template>
          {{ $t(`${TRANSLATION_KEY_PREFIX}.navigatePrevious`) }}
        </AppTooltip>
        <AppTooltip :disabled="!nextItem">
          <template #activator="{ props }">
            <app-button :disabled="!nextItem"
                        data-locator="next-item-nav-button"
                        type="icon"
                        v-bind="props"
                        @click="onNextItem"
            >
              <app-material-icon
                name="keyboard_arrow_down"
                size="sm"
                :color="nextItem ? 'icon-primary' : 'icon-disabled'"
              />
            </app-button>
          </template>
          {{ $t(`${TRANSLATION_KEY_PREFIX}.navigateNext`) }}
        </AppTooltip>
      </nav>
    </template>

    <template #tab-details>
      <ChecklistPaneDetails @data-unsaved="onDataUnsaved" />
    </template>
    <template #tab-files>
      <ChecklistPaneFiles />
    </template>
    <template #tab-comments>
      <ChecklistPaneComments @data-unsaved="onDataUnsaved" />
    </template>
    <template #tab-rights>
      <ChecklistPaneRights />
    </template>
  </app-drawer>

  <!-- Sibling of the drawer rather than inside it: this is a dialog, not a drawer action -->
  <ChecklistPaneDataUnsavedDialog
    v-if="isChecklistPaneDataUnsavedDialogOpen"
    :tab="tabWithDataUnsaved"
    @close="onCloseDialog"
    @confirm="onConfirmClosePane"
  />
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import ChecklistPaneDataUnsavedDialog from '@/project/checklist/dialogs/ChecklistPaneDataUnsavedDialog.vue'
import ChecklistPaneComments from '@/project/checklist/panes/ChecklistPaneComments.vue'
import ChecklistPaneDetails from '@/project/checklist/panes/ChecklistPaneDetails.vue'
import ChecklistPaneFiles from '@/project/checklist/panes/ChecklistPaneFiles.vue'
import ChecklistPaneRights from '@/project/checklist/panes/ChecklistPaneRights.vue'
import { CHANGE_CHECKLIST_PANE_TASKS, CLOSE_CHECKLIST_PANE, GET_CHECKLIST, GET_TASK_DETAILS } from '@/store/modules/checklist/action_types'
import { SET_CHECKLIST_PANE_TAB } from '@/store/modules/checklist/mutation_types'

const TRANSLATION_KEY_PREFIX = 'project.checklist.panes.ChecklistPane'

export default defineComponent({
  name: 'ChecklistPane',
  components: {
    AppTooltip,
    ChecklistPaneDataUnsavedDialog,
    ChecklistPaneComments,
    ChecklistPaneDetails,
    ChecklistPaneFiles,
    ChecklistPaneRights,
  },
  data () {
    return {
      TRANSLATION_KEY_PREFIX,
      hasNavigateBetweenTasks: false,
      isChecklistPaneDataUnsavedDialogOpen: false,
      tabWithDataUnsaved: null,
      tabs: [
        {
          label: this.$t(`${TRANSLATION_KEY_PREFIX}.tabDetails`),
          value: 'details',
        },
        {
          label: this.$t(`${TRANSLATION_KEY_PREFIX}.tabFiles`),
          value: 'files',
        },
        {
          label: this.$t(`${TRANSLATION_KEY_PREFIX}.tabComments`),
          value: 'comments',
        },
        {
          label: this.$t(`${TRANSLATION_KEY_PREFIX}.tabRights`),
          value: 'rights',
        },
      ],
    }
  },
  computed: {
    ...mapState('checklist', ['checklistPane']),
    ...mapGetters('room', ['isCurrentUserPm', 'roomMnemo']),
    canNavigate () {
      return Boolean(this.previousItem || this.nextItem)
    },
    displayPaneTitle() {
      return this.currentTask ? `${ this.currentTask?.positionDisplay || '' } ${ this.currentTask?.title || '' }` : ''
    },
    displayTabs() {
      return this.isCurrentUserPm ? this.tabs : this.tabs.filter((tab) => tab.value !== 'rights')
    },
    currentTask() {
      return this.checklistPane.tasks[0]
    },
    nextItem() {
      return this.getSiblingTask('next')
    },
    previousItem() {
      return this.getSiblingTask('prev')
    },
    tab: {
      get () {
        return this.checklistPane.tab
      },
      set (val) {
        this.SET_CHECKLIST_PANE_TAB(val)
      },
    },
  },
  methods: {
    ...mapActions('checklist', [ CHANGE_CHECKLIST_PANE_TASKS, CLOSE_CHECKLIST_PANE, GET_CHECKLIST, GET_TASK_DETAILS ]),
    ...mapMutations('checklist', [ SET_CHECKLIST_PANE_TAB ]),
    getSiblingTask(direction) {
      const parentTasks = this.checklistPane.parent?.children.filter(item => item.type === 'task') || []
      const currentTaskIndex = parentTasks.findIndex(task => task.id === this.currentTask?.id)

      if (currentTaskIndex === -1) return null

      const siblingIndex = direction === 'next' ? currentTaskIndex + 1 : currentTaskIndex - 1
      return parentTasks[siblingIndex] || null
    },
    onCloseDialog () {
      this.isChecklistPaneDataUnsavedDialogOpen = false
    },
    onClosePane () {
      if(this.tabWithDataUnsaved) {
        this.isChecklistPaneDataUnsavedDialogOpen = true
      } else {
        this.refreshChecklistData()
        this.CLOSE_CHECKLIST_PANE()
      }
    },
    onConfirmClosePane () {
      this.tabWithDataUnsaved = null
      this.onCloseDialog()
      this.refreshChecklistData()
      this.CLOSE_CHECKLIST_PANE()
    },
    onDataUnsaved (dataUnsaved) {
      this.tabWithDataUnsaved = dataUnsaved
    },
    onNextItem() {
      if(this.nextItem) {
        this.CHANGE_CHECKLIST_PANE_TASKS([this.nextItem])
        this.hasNavigateBetweenTasks = true
      }
    },
    onPreviousItem() {
      if(this.previousItem) {
        this.CHANGE_CHECKLIST_PANE_TASKS([this.previousItem])
        this.hasNavigateBetweenTasks = true
      }
    },
    async refreshChecklistData() {
      if (!this.hasNavigateBetweenTasks) {
        await this.GET_TASK_DETAILS({
          mnemo: this.roomMnemo,
          taskId: this.currentTask.id,
        })
      } else {
        await this.GET_CHECKLIST(this.roomMnemo)
      }

      this.hasNavigateBetweenTasks = false
    },
  },
})
</script>
